import type { fabric } from 'fabric';

/*
 * Annotation coordinates
 *
 * Annotations are saved in canvas coordinates without the viewport transform,
 * which is a zoom and an offset: coordinate × zoom = CSS pixels from the
 * asset's top left corner as shown, and the offset is where that corner is on
 * the canvas (the canvas may cover only the visible part of the asset). Every
 * asset type has its own coordinate unit, the same on every screen size and
 * zoom.
 *
 * Changing any constant here moves every saved annotation of its asset type.
 */

/**
 * Images and documents: one coordinate is this many asset pixels.
 * - Images: natural pixels of the loaded file (the proxy)
 * - Documents: pixels of the page at its base size (`PDF_PAGE_BASE_SIZE`),
 *   relative to the page's width, so every page is as wide in coordinates as
 *   the first one
 */
export const ASSET_PIXELS_PER_COORDINATE = 10;

/**
 * Video and audio: the player's width in coordinates, whatever its size on
 * screen. Its height follows from the player's aspect ratio
 * (`PLAYER_ASPECT_RATIO`), so letterboxed videos stay in place too.
 */
export const PLAYER_WIDTH_IN_COORDINATES = 300;
export const PLAYER_ASPECT_RATIO = '16:9';

/** Annotation zoom of an image or a document page shown at this zoom */
export function getAssetAnnotationZoom(zoomFactor: number) {
  return zoomFactor * ASSET_PIXELS_PER_COORDINATE;
}

/** Annotation zoom of a player this many CSS pixels wide */
export function getPlayerAnnotationZoom(playerWidth: number) {
  return playerWidth / PLAYER_WIDTH_IN_COORDINATES;
}

export const ANNOTATION_ZOOM_CHANGED_EVENT = 'viewport:zoom:changed';

/**
 * Applies the annotation zoom, and where the asset's top left corner is on
 * the canvas (CSS pixels): a canvas can cover only part of the asset (see
 * annotationLayout). Kept as it is when not given.
 *
 * When the zoom changes, the canvas fires `ANNOTATION_ZOOM_CHANGED_EVENT`, so
 * pins and strokes are fitted to it.
 */
export function setAnnotationZoom(
  canvas: fabric.Canvas,
  zoom: number,
  assetOffset?: { x: number; y: number }
) {
  // E.g. a player measured while hidden
  if (!Number.isFinite(zoom) || zoom <= 0) return;
  const isChanged = canvas.getZoom() !== zoom;
  const transform = canvas.viewportTransform;
  const x = assetOffset?.x ?? transform?.[4] ?? 0;
  const y = assetOffset?.y ?? transform?.[5] ?? 0;
  canvas.setViewportTransform([zoom, 0, 0, zoom, x, y]);
  if (isChanged) canvas.fire(ANNOTATION_ZOOM_CHANGED_EVENT);
}

type Size = { width: number; height: number };

const annotationAreas = new WeakMap<fabric.Canvas, Size>();

/**
 * Sets the size of the whole asset in coordinates, for canvases covering only
 * part of it (see annotationLayout)
 */
export function setAnnotationArea(canvas: fabric.Canvas, area: Size) {
  annotationAreas.set(canvas, area);
}

/** Whether the canvas covers only part of the asset (see setAnnotationArea) */
export function hasAnnotationArea(canvas: fabric.Canvas) {
  return annotationAreas.has(canvas);
}

/**
 * Size of the whole asset in coordinates (e.g. rotating annotations or placing
 * document highlights). Without one set, the canvas covers the whole asset.
 */
export function getAnnotationArea(canvas: fabric.Canvas): Size {
  const area = annotationAreas.get(canvas);
  if (area) return area;
  const zoom = canvas.getZoom() || 1;
  return { width: canvas.getWidth() / zoom, height: canvas.getHeight() / zoom };
}

/** Where the whole asset is on screen (client coordinates) */
export function getAnnotationAreaClientRect(canvas: fabric.Canvas) {
  const canvasRect = canvas.getElement().getBoundingClientRect();
  const transform = canvas.viewportTransform ?? [1, 0, 0, 1, 0, 0];
  const zoom = canvas.getZoom() || 1;
  const area = getAnnotationArea(canvas);
  return {
    left: canvasRect.left + transform[4],
    top: canvasRect.top + transform[5],
    width: area.width * zoom,
    height: area.height * zoom
  };
}
